import moment from "moment";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { AthleteLinks } from "../../../../parts/athlete-links";
import { AthleteSelect } from "../../../../parts/inputs/athlete-select-new";
import getAvatarUrl from "../../../../utils/get-avatar-url";
import NavigationComponent from "../components/NavigationComponent";
import { ReportsBackHandler } from "../components/ReportsBackHandler";
import {
  defaultFormat,
  filterByWeekRange,
  findFrontLeftBack,
  getDaysRange,
  mapDayRanges,
  mapPhotos,
  mapWorkout,
  sortByUtcDate,
} from "./body-composition.helper";
import BodyMeasureStats from "./components/BodyMeasureStats";
import BodyWeightStats from "./components/BodyWeightStats";
//import the components from the body composition folder
import DateDropdownNew from "./components/DateDropdownNew";
import NutritionStats from "./components/NutritionStats";
import ProgressPhotosContainer from "./components/ProgressPhotosContainer";
import SleepStats from "./components/SleepStats";
import StepsStats from "./components/StepsStats";
import ThisWeekStats from "./components/ThisWeekStats";
import WeekSelectionBar from "./components/WeekSelectionBar";
import WorkloadStats from "./components/WorkloadStats";

const BodyComposition = ({ locals, start, end, selectedWeek, weeksNumber }) => {
  const { t } = useTranslation("athlete-portal");
  const { query, pathname } = locals;
  const organizationCoach = query.organizationCoach || undefined;
  const organizationCoachParam = organizationCoach
    ? `&organizationCoach=${organizationCoach}`
    : "";
  const { date, athlete } = query;
  const [startDate, setStartDate] = useState(start);
  const [endDate, setEndDate] = useState(end);
  const [dailyStats, setDailyStats] = useState([]);
  const [activeExercises, setActiveExercises] = useState([]);
  const [threeMonthData, setThreeMonthData] = useState([]);
  // const [dateDropdown, setDateDropdown] = useState(false);
  const [previousWeek, setPreviousWeek] = useState([]);

  const calendarDay = locals.activeUser.startingCalendarDay || 0;
  const units = locals.activeUser.units || "metric";

  const rangeOfDays = getDaysRange(startDate, endDate);

  const daysRange = rangeOfDays.map(mapDayRanges);

  const foundPhotos = findFrontLeftBack(dailyStats.map(mapPhotos));

  useEffect(() => {
    setStartDate(start);
    setEndDate(end);
  }, [start, end]);
  //assign dailyStats once

  useEffect(() => {
    const withWorkoutData = locals.dailyStats
      .map((data) => mapWorkout(locals.activeExercises, data))
      .sort(sortByUtcDate);

    const dailyStats = withWorkoutData
      .filter((data) => filterByWeekRange(data, { start, end }))
      .sort(sortByUtcDate);

    const previousStart = moment(start)
      .subtract(7, "days")
      .format(defaultFormat);
    const previousEnd = moment(end).subtract(7, "days").format(defaultFormat);

    const previousWeek = withWorkoutData
      .filter((data) =>
        filterByWeekRange(data, {
          start: previousStart,
          end: previousEnd,
        })
      )
      .sort(sortByUtcDate);

    const activeExercisesForWeek = locals.activeExercises
      .filter((exercise) => filterByWeekRange(exercise, { start, end }))
      .sort(sortByUtcDate);

    setDailyStats(dailyStats);
    setPreviousWeek(previousWeek);
    setThreeMonthData(withWorkoutData.sort(sortByUtcDate));
    setActiveExercises(activeExercisesForWeek);
  }, [locals.dailyStats]);

  return (
    <main role="reports-wrapper">
      <NavigationComponent
        query={query}
        textKey="nav.calendarDayView"
        pathName={pathname}
        classNames="mobile-hidden"
      />

      <div className="athlete-section-header only-show-mobile">
        <AthleteLinks
          athlete={athlete}
          pathName={pathname}
          classNames={"only-show-mobile"}
          query={query}
        />
        <div className="athlete-select dropdown-athlete">
          <div
            className="avatar"
            style={{
              backgroundImage: `url(${getAvatarUrl(athlete?._id)})`,
            }}
          />
          <AthleteSelect locals={locals} />
        </div>
      </div>

      <article id="body-composition" role="report-content">
        <header>
          <div className="top-header only-show-mobile">
            <ReportsBackHandler
              title={t("bodyComposition.title")}
              locals={locals}
            />
            <DateDropdownNew
              startDate={startDate}
              endDate={endDate}
              calendarDay={calendarDay}
            />
          </div>
          <hgroup className="mobile-hidden">
            <h2>{t("bodyComposition.title")}</h2>
          </hgroup>
          <hgroup>
            <DateDropdownNew
              startDate={startDate}
              endDate={endDate}
              calendarDay={calendarDay}
            />
            <WeekSelectionBar
              daysOfWeek={daysRange}
              selectedWeek={selectedWeek}
              weeksNumber={weeksNumber}
              startDate={startDate}
              endDate={endDate}
              calendarDay={calendarDay}
            />
          </hgroup>
        </header>

        {Object.keys(foundPhotos).length > 0 ? (
          <ProgressPhotosContainer
            bodyPhotos={foundPhotos}
            date={date}
            athlete={athlete}
            organizationCoachParam={organizationCoachParam}
          />
        ) : null}

        <section role="composition-top-graphs-wrapper">
          <BodyWeightStats
            dailyStats={dailyStats}
            date={date}
            athlete={athlete}
            threeMonthData={threeMonthData}
            start={startDate}
            end={endDate}
            units={units}
            organizationCoachParam={organizationCoachParam}
          />
          <div className="composition-top-graphs-separation">
            <SleepStats
              data={dailyStats}
              date={date}
              athlete={athlete}
              previousWeek={previousWeek}
              organizationCoachParam={organizationCoachParam}
            />
            <StepsStats
              data={dailyStats}
              date={date}
              athlete={athlete}
              previousWeek={previousWeek}
              organizationCoachParam={organizationCoachParam}
            />
          </div>
        </section>
        <NutritionStats
          stats={dailyStats}
          dateFrom={start}
          dateTo={end}
          date={date}
          athlete={athlete}
          nutritionPlans={locals.nutritionPlans}
          calendarDay={calendarDay}
          organizationCoachParam={organizationCoachParam}
          activeExercises={locals.activeExercises}
        />
        <div className="composition-bottom-graphs-container">
          <WorkloadStats />
          <BodyMeasureStats
            data={threeMonthData}
            date={date}
            athlete={athlete}
            units={units}
            startingCalendarDay={calendarDay}
            activeExercises={activeExercises}
            organizationCoachParam={organizationCoachParam}
            useLastMeasure={true}
          />
        </div>
        <ThisWeekStats
          athlete={athlete}
          startingCalendarDay={calendarDay}
          activeExercises={activeExercises}
          organizationCoachParam={organizationCoachParam}
        />
      </article>
    </main>
  );
};

export default BodyComposition;
